import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner, Empty } from "@/components/common";
import { FileSignature, Plus, X, UserCheck, AlertTriangle, BellRing } from "lucide-react";
import { AvisoFirmas } from "@/components/AvisoFirmas";
import { AvisoGerencia } from "@/components/AvisoGerencia";

const ESTADOS = {
  condiciones_pendientes: { label: "Condiciones pendientes", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  condiciones_validadas: { label: "Condiciones validadas", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  documentos_emitidos: { label: "Documentos emitidos", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  en_firma: { label: "En firma", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pendiente_gerencia: { label: "Pendiente firma de Gerencia", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  firmado: { label: "Firmado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export function ContratacionEstadoBadge({ estado }) {
  const s = ESTADOS[estado] || ESTADOS.condiciones_pendientes;
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

function NuevaModal({ onClose, onCreated }) {
  const [exps, setExps] = useState(null);
  const [saving, setSaving] = useState("");
  useEffect(() => {
    api.get("/expedientes", { params: { estado: "validado" } })
      .then((r) => setExps(r.data.filter((e) => e.vinculacion_id)))
      .catch(() => setExps([]));
  }, []);
  const iniciar = async (eid) => {
    setSaving(eid);
    try { const { data } = await api.post("/contrataciones", { expediente_id: eid }); toast.success("Contratación iniciada"); onCreated(data.id); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setSaving(""); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Iniciar contratación</h3><button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
        <p className="mb-4 text-sm text-slate-500">Elige un expediente validado por HUMAN. La carga inicial no genera contrataciones.</p>
        {exps === null ? <div className="flex h-24 items-center justify-center"><Spinner className="h-5 w-5 text-vino-700" /></div>
          : exps.length === 0 ? <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">No hay expedientes validados disponibles.</p>
          : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {exps.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`nueva-exp-${e.id}`}>
                  <div><p className="text-sm font-semibold text-slate-800">{e.nombre}</p><p className="text-xs text-slate-400">{e.numero_documento} · {e.empresa_cliente || "—"}</p></div>
                  <button onClick={() => iniciar(e.id)} disabled={!!saving} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800 disabled:opacity-60">{saving === e.id ? <Spinner /> : <Plus className="h-3.5 w-3.5" />} Iniciar</button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export default function Contrataciones() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => { setRows(null); api.get("/contrataciones").then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(load, []);
  const abrir = (r) => { if (r.aviso_firmas) api.patch(`/contrataciones/${r.id}/aviso-firmas/visto`).catch(() => {}); navigate(`/contrataciones/${r.id}`); };
  const avisos = (rows || []).filter((r) => r.aviso_firmas);
  const esperanGerencia = (rows || []).filter((r) => r.faltan_gerencia);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Contrataciones</h1>
          <p className="mt-1 text-sm text-slate-500">Condiciones laborales, emisión de documentos, firmas y afiliaciones (HUMAN).</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/contrataciones/terminaciones")} data-testid="btn-terminaciones" className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-vino-700 hover:bg-vino-50">Terminación de contratos</button>
          <button onClick={() => setShowNew(true)} data-testid="btn-nueva-contratacion" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800"><Plus className="h-4 w-4" /> Iniciar contratación</button>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <FileSignature className="mt-0.5 h-5 w-5 shrink-0" />
        <p>Una contratación solo avanza a firma tras validar las condiciones y emitir documentos desde plantillas aprobadas. Los empleados de <strong>carga inicial</strong> permanecen en control administrativo, sin generación contractual.</p>
      </div>

      {avisos.length > 0 && <AvisoFirmas avisos={avisos} onAbrir={abrir} />}
      {esperanGerencia.length > 0 && <AvisoGerencia rows={esperanGerencia} onAbrir={abrir} />}

      {rows === null ? <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>
        : rows.length === 0 ? <Empty icon={FileSignature} title="Sin contrataciones" subtitle="Inicia una contratación desde un expediente validado por HUMAN." />
        : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm" data-testid="tabla-contrataciones">
                <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Trabajador</th><th className="px-4 py-3 font-semibold">Cargo / Empresa</th>
                  <th className="px-4 py-3 font-semibold">Documentos</th><th className="px-4 py-3 font-semibold">ARL</th><th className="px-4 py-3 font-semibold">Estado</th><th className="px-4 py-3" />
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-vino-50/40" onClick={() => abrir(r)} data-testid={`fila-contratacion-${r.id}`}>
                      <td className="px-4 py-3"><p className="font-semibold text-slate-800">{r.nombre}</p><p className="text-xs text-slate-400">{r.identificacion} · {r.numero_contrato}</p>{r.aviso_firmas && <span className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`aviso-firmas-${r.id}`}><BellRing className="h-3 w-3" /> Firmó todo · nuevo</span>}{r.alerta_sst && <span className="mt-1 inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`alerta-sst-${r.id}`}><AlertTriangle className="h-3 w-3" /> SG-SST: marcó NO</span>}</td>
                      <td className="px-4 py-3 text-slate-600">{r.cargo || "—"}<p className="text-xs text-slate-400">{r.empresa_usuaria || "—"}</p></td>
                      <td className="px-4 py-3 text-slate-600">{r.documentos_firmados}/{r.documentos_total} firmados</td>
                      <td className="px-4 py-3">{r.afiliaciones_pendientes > 0 ? <span className="text-xs text-amber-600">Pendiente</span> : <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><UserCheck className="h-3.5 w-3.5" /> Registrada</span>}</td>
                      <td className="px-4 py-3"><ContratacionEstadoBadge estado={r.estado} /></td>
                      <td className="px-4 py-3 text-right"><span className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-vino-700">Abrir</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showNew && <NuevaModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/contrataciones/${id}`)} />}
    </div>
  );
}
